// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject, signal } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  ReportIssueEntity,
  ReportIssueRequest,
  ReportIssueResponse,
} from '../entities/report-issue.entity';
import { ReportIssueApiService } from './api/report-issue-api.service';

export interface ReportIssueState {
  submitting: boolean;
  submitted: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ReportIssueService {
  private readonly reportIssueApi = inject(ReportIssueApiService);

  private readonly _state = signal<ReportIssueState>({
    submitting: false,
    submitted: false,
    error: null,
  });

  readonly state = this._state.asReadonly();

  submitReport(report: ReportIssueRequest): Observable<ReportIssueResponse> {
    const validation = ReportIssueEntity.validate(report);

    if (!validation.isValid) {
      this._state.set({
        submitting: false,
        submitted: false,
        error: validation.errors.join(', '),
      });
      return throwError(() => new Error(validation.errors.join(', ')));
    }

    this._state.set({ submitting: true, submitted: false, error: null });

    const payload = ReportIssueEntity.toApiPayload(report);

    return this.reportIssueApi.submitReport(payload).pipe(
      map((response) => {
        this._state.set({ submitting: false, submitted: true, error: null });
        return response;
      }),
      catchError((error) => {
        // For rate limiting (429) and other API errors, use the backend message directly
        const errorMessage = this.extractErrorMessage(error);

        this._state.set({
          submitting: false,
          submitted: false,
          error: errorMessage,
        });

        // Create error object and throw directly
        const errorObj = { message: errorMessage };
        return throwError(errorObj);
      })
    );
  }

  resetState(): void {
    this._state.set({
      submitting: false,
      submitted: false,
      error: null,
    });
  }

  getConfig() {
    return ReportIssueEntity.getConfig();
  }

  getMetricDisplayName(metricType: string): string {
    return ReportIssueEntity.getMetricDisplayName(metricType);
  }

  /**
   * Extract error message from HTTP error response
   * Shows specific messages for actionable errors, generic message for others
   */
  private extractErrorMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return ReportIssueEntity.getConfig().display.errorMessage;
    }

    const { status, error: responseBody } = error;
    const backendMessage = responseBody?.message;

    // Rate limiting (429) - show backend message
    if (status === 429 && backendMessage) {
      return backendMessage;
    }

    // Validation errors (400) - show what's wrong so user can fix it
    if (status === 400 && backendMessage) {
      return backendMessage;
    }

    // Unauthorized (401) - session expired
    if (status === 401) {
      return 'Your session has expired. Please refresh the page and try again.';
    }

    // All other errors - generic message
    return ReportIssueEntity.getConfig().display.errorMessage;
  }

  /**
   * Validates that the control value is not empty or whitespace-only
   */
  static notWhitespaceOnly(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value !== 'string') {
      return null;
    }
    return value.trim().length === 0 ? { whitespace: true } : null;
  }

  /**
   * Validates minimum length after trimming whitespace
   */
  static trimmedMinLength(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value !== 'string' || value.trim().length === 0) {
        return null; // Let notWhitespaceOnly handle empty values
      }
      const trimmedLength = value.trim().length;
      return trimmedLength < minLength
        ? { trimmedMinLength: { requiredLength: minLength, actualLength: trimmedLength } }
        : null;
    };
  }

  /**
   * Validates maximum length after trimming whitespace
   */
  static trimmedMaxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value !== 'string') {
        return null;
      }
      const trimmedLength = value.trim().length;
      return trimmedLength > maxLength
        ? { trimmedMaxLength: { requiredLength: maxLength, actualLength: trimmedLength } }
        : null;
    };
  }
}

